/**
 * GameEngine component
 *
 * The main game view with mobile/tablet support.
 * Responsive canvas with touch input zones.
 */

import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGameLoop } from '../../hooks/useGameLoop';
import { useAudio } from '../../hooks/useAudio';
import { useMidi } from '../../hooks/useMidi';
import { useTouchInput } from '../../hooks/useTouchInput';
import { useResponsiveCanvas } from '../../hooks/useResponsiveCanvas';
import { HitZone } from './HitZone';
import { TouchZones } from './TouchZones';
import { ScoreDisplay } from './ScoreDisplay';
import { LoadingBar } from './LoadingBar';
import { AudioInitOverlay } from './AudioInitOverlay';
import { KEY_TO_LANE } from '../../types';
import { computeOverallAccuracy, accuracyToGrade } from '../../utils/timing';
import api from '../../services/authAPI';

interface GameEngineProps {
  trackId?: string;
}

// Warm color palette for lanes
const WARM_LANE_COLORS = [
  '#f5a623', // amber
  '#e8b86d', // gold
  '#e07a5f', // coral
  '#f5a623', // amber
  '#e8b86d', // gold
  '#e07a5f', // coral
];

export const GameEngine: React.FC<GameEngineProps> = ({ trackId }) => {
  const navigate = useNavigate();

  // Responsive canvas
  const { containerRef, canvasRef, dimensions, isMobile, updateDimensions } =
    useResponsiveCanvas();

  // Game loop - use our responsive canvas ref
  const {
    gameState,
    phase,
    startGame,
    stopGame,
    pauseGame,
    resumeGame,
    onAudioTimeUpdate,
    setSpeedMultiplier,
    countdownBeat,
  } = useGameLoop();

  const {
    loadAudio,
    startPlayback,
    stopPlayback,
    pausePlayback,
    resumePlayback,
    setPlaybackRate,
    currentTimeMs,
    isLoaded: audioLoaded,
    loadingProgress: audioProgress,
    isLoading: audioIsLoading,
    error: audioError,
    playMetronomeClick,
    needsUserInteraction,
    initAudioContext,
  } = useAudio();

  const SPEED_OPTIONS = [0.5, 0.75, 1, 1.25, 1.5] as const;
  const [speed, setSpeed] = useState<number>(1);

  const {
    midiTrack,
    gameNotes,
    audioUrl: trackAudioUrl,
    isLoading: midiLoading,
    error: midiError,
    loadTrack,
    loadDemoTrack,
    sectionStart,
    sectionEnd,
  } = useMidi();

  const [activeLanes, setActiveLanes] = useState<Set<number>>(new Set());
  const [lastRatings, setLastRatings] = useState<Array<'PERFECT' | 'GOOD' | 'MISS' | null>>(
    Array(6).fill(null)
  );
  const [lastRating, setLastRating] = useState<'PERFECT' | 'GOOD' | 'MISS' | null>(null);

  const prevScoreRef = useRef(0);
  const prevComboRef = useRef(0);
  const rafDrawRef = useRef<number>(0);
  const shouldAutoStartRef = useRef(false);

  // Touch input handling
  const handleLanePress = useCallback((lane: number) => {
    // Simulate keyboard input for this lane
    const event = new KeyboardEvent('keydown', { key: Object.keys(KEY_TO_LANE).find(
      k => KEY_TO_LANE[k] === lane
    ) || '' });
    window.dispatchEvent(event);
    
    // Auto-release after short delay
    setTimeout(() => {
      const upEvent = new KeyboardEvent('keyup', { key: Object.keys(KEY_TO_LANE).find(
        k => KEY_TO_LANE[k] === lane
      ) || '' });
      window.dispatchEvent(upEvent);
    }, 100);
  }, []);

  const { 
    activeLanes: touchActiveLanes, 
    isTouchDevice, 
    isTouchMode, 
    touchZonesRef,
    setTouchMode 
  } = useTouchInput(handleLanePress, phase === 'playing');

  // Sync touch active lanes with game state
  useEffect(() => {
    if (isTouchMode) {
      setActiveLanes(touchActiveLanes);
    }
  }, [touchActiveLanes, isTouchMode]);

  // Load track + audio
  useEffect(() => {
    const loadData = async () => {
      if (trackId !== undefined) {
        await loadTrack(trackId);
      } else {
        loadDemoTrack();
      }
    };
    void loadData();
  }, [trackId]);

  useEffect(() => {
    const loadAudioData = async () => {
      if (trackAudioUrl) {
        await loadAudio(trackAudioUrl);
      }
    };
    void loadAudioData();
  }, [trackAudioUrl]);

  useEffect(() => {
    onAudioTimeUpdate(currentTimeMs);
  }, [currentTimeMs, onAudioTimeUpdate]);

  // Re-measure canvas when audio finishes loading (canvas becomes visible)
  useEffect(() => {
    if (!audioIsLoading) {
      updateDimensions();
    }
  }, [audioIsLoading, updateDimensions]);

  useEffect(() => {
    if (gameState.score !== prevScoreRef.current || gameState.combo !== prevComboRef.current) {
      prevScoreRef.current = gameState.score;
      prevComboRef.current = gameState.combo;
    }
  }, [gameState.score, gameState.combo]);

  useEffect(() => {
    const judged = gameState.notes.filter((n) => n.judged && n.rating);
    if (judged.length === 0) return;
    const latest = judged[judged.length - 1];
    if (latest.rating) {
      setLastRating(latest.rating);
      setLastRatings((prev) => {
        const updated = [...prev];
        updated[latest.lane] = latest.rating ?? null;
        return updated;
      });
    }
  }, [gameState.notes]);

  useEffect(() => {
    if (phase === 'finished') {
      stopPlayback();
      const resultsState = {
        score: gameState.score,
        maxCombo: gameState.maxCombo,
        perfectCount: gameState.perfectCount,
        goodCount: gameState.goodCount,
        missCount: gameState.missCount,
        trackTitle: midiTrack?.title,
        trackArtist: midiTrack?.artist,
        bpm: midiTrack?.bpm,
      };
      const submitAndNavigate = async () => {
        if (trackId) {
          try {
            const totalNotes = gameState.perfectCount + gameState.goodCount + gameState.missCount;
            const accuracy = computeOverallAccuracy(gameState.perfectCount, gameState.goodCount, gameState.missCount);
            const grade = accuracyToGrade(accuracy);
            await api.post(`/tracks/${trackId}/score`, {
              score: gameState.score,
              accuracy,
              maxCombo: gameState.maxCombo,
              perfectCount: gameState.perfectCount,
              goodCount: gameState.goodCount,
              missCount: gameState.missCount,
              totalNotes,
              grade,
            });
          } catch (err) {
            console.error('Failed to submit score:', err);
          }
        }
        // Small delay so the "finished" state is visible before navigating
        await new Promise((r) => setTimeout(r, 500));
        navigate('/results', { state: resultsState });
      };
      void submitAndNavigate();
    }
  }, [phase, stopPlayback, gameState, midiTrack, trackId, navigate]);

  useEffect(() => {
    if (phase === 'countdown' && countdownBeat > 0) {
      playMetronomeClick(countdownBeat === 4);
    }
  }, [phase, countdownBeat, playMetronomeClick]);

  useEffect(() => {
    if (sectionEnd !== null && phase === 'playing' && currentTimeMs / 1000 >= (sectionEnd - sectionStart) + 0.5) {
      stopPlayback();
      stopGame();
    }
  }, [currentTimeMs, sectionEnd, sectionStart, phase, stopPlayback, stopGame]);

  // Keyboard feedback (only when not in touch mode)
  useEffect(() => {
    if (isTouchMode) return;
    
    const onDown = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      if (key in KEY_TO_LANE) {
        setActiveLanes((prev) => {
          const next = new Set(prev);
          next.add(KEY_TO_LANE[key]);
          return next;
        });
      }
    };
    const onUp = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      if (key in KEY_TO_LANE) {
        setActiveLanes((prev) => {
          const next = new Set(prev);
          next.delete(KEY_TO_LANE[key]);
          return next;
        });
      }
    };
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
    };
  }, [isTouchMode]);

  // Canvas rendering with piano-style visualization (like the YouTube video)
  const renderFrame = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const W = dimensions.width;
    const H = dimensions.height;
    const scale = dimensions.scale;
    const LANE_COUNT = 6;
    const LANE_WIDTH = W / LANE_COUNT;
    const HIT_LINE_Y = H - 100 * scale; // 100px from bottom for piano key area
    const NOTE_HEIGHT = 18 * scale;
    const NOTE_WIDTH = LANE_WIDTH * 0.6;
    const KEY_HEIGHT = 60 * scale;

    // Clear with dark background
    ctx.fillStyle = '#050505';
    ctx.fillRect(0, 0, W, H);

    // Draw subtle gradient background
    const bgGrad = ctx.createLinearGradient(0, 0, 0, H);
    bgGrad.addColorStop(0, '#080808');
    bgGrad.addColorStop(0.5, '#0a0a0a');
    bgGrad.addColorStop(1, '#050505');
    ctx.fillStyle = bgGrad;
    ctx.fillRect(0, 0, W, H);

    // Draw vertical lane lines (subtle)
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.03)';
    ctx.lineWidth = 1;
    for (let i = 0; i <= LANE_COUNT; i++) {
      const x = i * LANE_WIDTH;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, HIT_LINE_Y);
      ctx.stroke();
    }

    // Calculate note positions
    const speed = 0.45 * scale * 1000; // pixels per second, scaled
    const visibleNotes = gameState.notes
      .filter(n => !n.judged)
      .map(note => {
        const timeDiff = note.time - gameState.currentTime;
        const y = HIT_LINE_Y - (timeDiff * speed / 1000);
        return { ...note, y };
      })
      .filter(n => n.y > -50 && n.y < H + 50);

    // Draw note trails (glowing streaks behind notes)
    for (const note of visibleNotes) {
      const cx = note.lane * LANE_WIDTH + LANE_WIDTH / 2;
      const trailLength = 120 * scale;
      const color = WARM_LANE_COLORS[note.lane];
      
      // Create gradient trail
      const trailGrad = ctx.createLinearGradient(cx, note.y, cx, note.y - trailLength);
      trailGrad.addColorStop(0, color + '60');
      trailGrad.addColorStop(0.3, color + '30');
      trailGrad.addColorStop(1, 'transparent');
      
      ctx.fillStyle = trailGrad;
      ctx.fillRect(cx - NOTE_WIDTH / 2, note.y - trailLength, NOTE_WIDTH, trailLength);
    }

    // Draw waveform line across the hit zone (like in the video)
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)';
    ctx.lineWidth = 1 * scale;
    ctx.beginPath();
    for (let x = 0; x < W; x += 2) {
      const waveY = HIT_LINE_Y - 5 * scale + Math.sin(x * 0.05 + Date.now() * 0.002) * 3 * scale;
      if (x === 0) ctx.moveTo(x, waveY);
      else ctx.lineTo(x, waveY);
    }
    ctx.stroke();

    // Draw the glowing hit line
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.lineWidth = 2 * scale;
    ctx.shadowColor = 'rgba(255, 255, 255, 0.5)';
    ctx.shadowBlur = 15 * scale;
    ctx.beginPath();
    ctx.moveTo(0, HIT_LINE_Y);
    ctx.lineTo(W, HIT_LINE_Y);
    ctx.stroke();
    ctx.shadowBlur = 0;

    // Draw piano key base area
    ctx.fillStyle = '#0a0a0a';
    ctx.fillRect(0, HIT_LINE_Y, W, KEY_HEIGHT);

    // Draw piano keys
    const labels = ['S', 'D', 'F', 'J', 'K', 'L'];
    for (let i = 0; i < LANE_COUNT; i++) {
      const cx = i * LANE_WIDTH + LANE_WIDTH / 2;
      const color = WARM_LANE_COLORS[i];
      const isActive = activeLanes.has(i);

      // Key background
      const keyY = HIT_LINE_Y + 5 * scale;
      const keyW = LANE_WIDTH * 0.85;
      const keyH = KEY_HEIGHT - 10 * scale;
      const keyX = i * LANE_WIDTH + (LANE_WIDTH - keyW) / 2;

      // Key glow when active
      if (isActive) {
        ctx.shadowColor = color;
        ctx.shadowBlur = 25 * scale;
        ctx.fillStyle = color + '40';
        ctx.fillRect(keyX - 2 * scale, keyY - 2 * scale, keyW + 4 * scale, keyH + 4 * scale);
        ctx.shadowBlur = 0;
      }

      // Key body
      const keyGrad = ctx.createLinearGradient(cx, keyY, cx, keyY + keyH);
      if (isActive) {
        keyGrad.addColorStop(0, color + '50');
        keyGrad.addColorStop(0.5, color + '30');
        keyGrad.addColorStop(1, color + '10');
      } else {
        keyGrad.addColorStop(0, '#1a1a1a');
        keyGrad.addColorStop(0.5, '#111111');
        keyGrad.addColorStop(1, '#0a0a0a');
      }
      ctx.fillStyle = keyGrad;
      
      // Draw rounded key
      const keyRadius = 4 * scale;
      ctx.beginPath();
      ctx.moveTo(keyX + keyRadius, keyY);
      ctx.lineTo(keyX + keyW - keyRadius, keyY);
      ctx.quadraticCurveTo(keyX + keyW, keyY, keyX + keyW, keyY + keyRadius);
      ctx.lineTo(keyX + keyW, keyY + keyH - keyRadius);
      ctx.quadraticCurveTo(keyX + keyW, keyY + keyH, keyX + keyW - keyRadius, keyY + keyH);
      ctx.lineTo(keyX + keyRadius, keyY + keyH);
      ctx.quadraticCurveTo(keyX, keyY + keyH, keyX, keyY + keyH - keyRadius);
      ctx.lineTo(keyX, keyY + keyRadius);
      ctx.quadraticCurveTo(keyX, keyY, keyX + keyRadius, keyY);
      ctx.closePath();
      ctx.fill();

      // Key border
      ctx.strokeStyle = isActive ? color + '80' : 'rgba(255, 255, 255, 0.08)';
      ctx.lineWidth = 1 * scale;
      ctx.stroke();

      // Key label
      ctx.fillStyle = isActive ? color : 'rgba(255, 255, 255, 0.5)';
      ctx.font = `600 ${14 * scale}px "Space Grotesk", sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText(labels[i], cx, keyY + keyH - 12 * scale);
    }

    // Helper to draw rounded rectangle
    const drawRoundedRect = (x: number, y: number, w: number, h: number, r: number) => {
      const radius = Math.min(r, w / 2, h / 2);
      ctx.beginPath();
      ctx.moveTo(x + radius, y);
      ctx.lineTo(x + w - radius, y);
      ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
      ctx.lineTo(x + w, y + h - radius);
      ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
      ctx.lineTo(x + radius, y + h);
      ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
      ctx.lineTo(x, y + radius);
      ctx.quadraticCurveTo(x, y, x + radius, y);
      ctx.closePath();
    };

    // Draw notes as rounded rectangles (piano bar style)
    for (const note of visibleNotes) {
      const cx = note.lane * LANE_WIDTH + LANE_WIDTH / 2;
      const color = WARM_LANE_COLORS[note.lane];
      const nx = cx - NOTE_WIDTH / 2;
      const ny = note.y - NOTE_HEIGHT / 2;
      
      // Note glow
      ctx.shadowColor = color;
      ctx.shadowBlur = 20 * scale;
      
      // Note body gradient
      const noteGrad = ctx.createLinearGradient(cx, ny, cx, ny + NOTE_HEIGHT);
      noteGrad.addColorStop(0, '#ffffff');
      noteGrad.addColorStop(0.3, color);
      noteGrad.addColorStop(1, color + '80');
      
      ctx.fillStyle = noteGrad;
      drawRoundedRect(nx, ny, NOTE_WIDTH, NOTE_HEIGHT, 6 * scale);
      ctx.fill();
      
      // Inner highlight
      ctx.shadowBlur = 0;
      ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
      drawRoundedRect(
        nx + 2 * scale,
        ny + 2 * scale,
        NOTE_WIDTH - 4 * scale,
        NOTE_HEIGHT / 2 - 2 * scale,
        3 * scale
      );
      ctx.fill();
    }

    // Hit particles (elegant thin line explosion effect)
    const RATING_COLORS: Record<string, string> = {
      PERFECT: '#81b29a',
      GOOD: '#e8b86d',
      MISS: '#e07a5f'
    };

    for (const p of gameState.particles) {
      const cx = p.lane * LANE_WIDTH + LANE_WIDTH / 2;
      const cy = HIT_LINE_Y - 8 * scale;
      const color = RATING_COLORS[p.rating] ?? '#ffffff';

      ctx.globalAlpha = p.opacity;
      ctx.strokeStyle = color;
      ctx.lineCap = 'round';
      
      // Elegant thin line explosion - radiating lines
      const numRays = 12;
      const maxRayLength = 40 * scale;
      
      for (let i = 0; i < numRays; i++) {
        // Distribute rays evenly with slight random offset
        const baseAngle = (i / numRays) * Math.PI * 2;
        const angleVariation = Math.sin(p.age * 0.3 + i) * 0.15;
        const angle = baseAngle + angleVariation;
        
        // Ray length expands outward then fades
        const rayProgress = Math.min(1, p.age / 8);
        const rayLength = maxRayLength * rayProgress * (1 - rayProgress * 0.3);
        
        if (rayLength > 2 * scale) {
          // Inner bright core
          const innerStart = 3 * scale * (1 - rayProgress * 0.5);
          const outerEnd = innerStart + rayLength;
          
          // Main ray - thin and elegant
          ctx.lineWidth = 1.5 * scale * (1 - rayProgress * 0.5);
          ctx.shadowColor = color;
          ctx.shadowBlur = 8 * scale * (1 - rayProgress);
          
          ctx.beginPath();
          ctx.moveTo(cx + Math.cos(angle) * innerStart, cy + Math.sin(angle) * innerStart);
          ctx.lineTo(cx + Math.cos(angle) * outerEnd, cy + Math.sin(angle) * outerEnd);
          ctx.stroke();
          
          // Secondary thinner ray for depth (slightly offset angle)
          if (p.rating === 'PERFECT') {
            ctx.lineWidth = 0.5 * scale * (1 - rayProgress);
            ctx.globalAlpha = p.opacity * 0.5;
            const secondaryAngle = angle + 0.05;
            const secondaryLength = rayLength * 0.7;
            ctx.beginPath();
            ctx.moveTo(cx, cy);
            ctx.lineTo(
              cx + Math.cos(secondaryAngle) * secondaryLength,
              cy + Math.sin(secondaryAngle) * secondaryLength
            );
            ctx.stroke();
            ctx.globalAlpha = p.opacity;
          }
        }
      }
      
      // Center core - small bright point that fades
      const coreSize = (6 - p.age * 0.8) * scale;
      if (coreSize > 0) {
        ctx.fillStyle = '#ffffff';
        ctx.shadowColor = color;
        ctx.shadowBlur = 15 * scale * (1 - p.age * 0.08);
        ctx.beginPath();
        ctx.arc(cx, cy, coreSize * 0.5, 0, Math.PI * 2);
        ctx.fill();
      }
      
      // Subtle ring expansion for PERFECT hits
      if (p.rating === 'PERFECT') {
        const ringRadius = 15 * scale + p.age * 5 * scale;
        const ringAlpha = p.opacity * (1 - p.age * 0.08) * 0.4;
        if (ringAlpha > 0) {
          ctx.globalAlpha = ringAlpha;
          ctx.strokeStyle = color;
          ctx.lineWidth = 0.8 * scale;
          ctx.shadowBlur = 5 * scale;
          ctx.beginPath();
          ctx.arc(cx, cy, ringRadius, 0, Math.PI * 2);
          ctx.stroke();
          ctx.globalAlpha = p.opacity;
        }
      }
      
      ctx.shadowBlur = 0;
      ctx.globalAlpha = 1;
    }
    ctx.textAlign = 'left';

    // Draw floating rating text (minimal, elegant)
    for (const p of gameState.particles) {
      const cx = p.lane * LANE_WIDTH + LANE_WIDTH / 2;
      const color = RATING_COLORS[p.rating] ?? '#ffffff';
      
      ctx.globalAlpha = p.opacity * 0.9;
      ctx.fillStyle = color;
      ctx.font = `500 ${13 * scale}px "Space Grotesk", sans-serif`;
      ctx.textAlign = 'center';
      ctx.letterSpacing = '0.1em';
      
      // Text floats upward gently
      const textY = HIT_LINE_Y - 40 * scale - p.age * 1.2 * scale;
      ctx.fillText(p.rating, cx, textY);
      ctx.letterSpacing = 'normal';
      ctx.globalAlpha = 1;
    }
    ctx.textAlign = 'left';

    rafDrawRef.current = requestAnimationFrame(renderFrame);
  }, [gameState, activeLanes, canvasRef, dimensions]);

  useEffect(() => {
    rafDrawRef.current = requestAnimationFrame(renderFrame);
    return () => cancelAnimationFrame(rafDrawRef.current);
  }, [renderFrame]);

  // Controls
  const handleSpeedChange = useCallback((rate: number) => {
    setSpeed(rate);
    setPlaybackRate(rate);
    setSpeedMultiplier(rate);
  }, [setPlaybackRate, setSpeedMultiplier]);

  const handleStart = useCallback(async () => {
    if (!midiTrack || gameNotes.length === 0) return;
    startGame(gameNotes, midiTrack.bpm);
    const beatMs = 60_000 / midiTrack.bpm;
    const countInMs = beatMs * 4;
    await new Promise((r) => setTimeout(r, countInMs));
    if (trackId !== undefined && audioLoaded) {
      await startPlayback(sectionStart);
    }
  }, [midiTrack, gameNotes, startGame, trackId, audioLoaded, startPlayback, sectionStart]);

  const handleStop = useCallback(() => {
    stopGame();
    stopPlayback();
  }, [stopGame, stopPlayback]);

  const handlePause = useCallback(() => {
    pauseGame();
    pausePlayback();
  }, [pauseGame, pausePlayback]);

  const handleResume = useCallback(async () => {
    resumeGame();
    await resumePlayback();
  }, [resumeGame, resumePlayback]);

  const isLoading = midiLoading || audioIsLoading;
  const hasError = midiError ?? audioError;
  // For demo mode (no trackId), audio is synthesized so we don't need audioLoaded
  const canStart = midiTrack && gameNotes.length > 0 && (audioLoaded || trackId === undefined) && !isLoading;

  // Handle audio init + auto-start from overlay
  const handleAudioInitAndStart = useCallback(async () => {
    shouldAutoStartRef.current = true;
    await initAudioContext();
    // Audio will start loading now, useEffect will handle auto-start when ready
  }, [initAudioContext]);

  // Auto-start game when audio finishes loading (after user clicked "Tap to Start")
  useEffect(() => {
    if (shouldAutoStartRef.current && canStart && phase === 'idle') {
      shouldAutoStartRef.current = false;
      void handleStart();
    }
  }, [canStart, phase, handleStart]);

  return (
    <div className="min-h-screen flex flex-col bg-studio-950">
      {/* Header - Responsive */}
      <div className={`flex items-center justify-between px-4 py-3 bg-studio-950 border-b border-studio-800 ${isMobile ? 'flex-wrap gap-2' : ''}`}>
        <div className="min-w-0">
          <h2 className="text-stone-100 font-semibold text-lg truncate">
            {midiTrack?.title ?? 'Loading...'}
          </h2>
          {midiTrack?.artist && (
            <p className="text-stone-500 text-sm truncate">{midiTrack.artist}</p>
          )}
        </div>
        
        <div className={`flex items-center gap-2 ${isMobile ? 'w-full justify-between' : 'gap-4'}`}>
          {/* Input mode toggle */}
          {isTouchDevice && (
            <button
              type="button"
              onClick={() => setTouchMode(!isTouchMode)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                isTouchMode 
                  ? 'bg-amber-500/20 text-amber-400 border border-amber-500/50' 
                  : 'bg-studio-800 text-stone-400 border border-studio-700'
              }`}
            >
              {isTouchMode ? 'Touch' : 'Keys'}
            </button>
          )}

          {/* Speed selector */}
          <div className="flex items-center gap-1 bg-studio-850 rounded-lg p-1">
            {!isMobile && <span className="text-stone-500 text-xs px-2">Speed</span>}
            {SPEED_OPTIONS.map((s) => (
              <button
                type="button"
                key={s}
                onClick={() => handleSpeedChange(s)}
                disabled={phase === 'playing' || phase === 'countdown'}
                className={`px-2 py-1 rounded text-xs font-semibold transition-all ${
                  speed === s ? 'bg-amber-500 text-studio-950' : 'text-stone-400 hover:text-stone-200'
                } ${(phase === 'playing' || phase === 'countdown') ? 'opacity-40 cursor-not-allowed' : ''}`}
              >
                {s}×
              </button>
            ))}
          </div>

          {!isMobile && midiTrack && (
            <span className="text-stone-500 text-sm font-mono">
              {Math.round(midiTrack.bpm * speed)} BPM
            </span>
          )}
          
          {/* Start button - hidden when we need user interaction (AudioInitOverlay handles it) */}
          {(phase === 'idle' || phase === 'finished') && !needsUserInteraction ? (
            <button
              type="button"
              onClick={() => void handleStart()}
              disabled={!canStart}
              className="px-4 py-2 bg-amber-500 text-studio-950 font-semibold rounded-lg hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              {audioIsLoading ? 'Loading...' : isLoading ? 'Loading...' : 'Start'}
            </button>
          ) : phase === 'paused' ? (
            <button
              type="button"
              onClick={() => void handleResume()}
              className="px-4 py-2 bg-sage-400 text-studio-950 font-semibold rounded-lg hover:bg-sage-300 transition-all"
            >
              Resume
            </button>
          ) : (
            <button
              type="button"
              onClick={handlePause}
              className="px-4 py-2 bg-studio-800 text-stone-300 font-semibold rounded-lg hover:bg-studio-700 transition-all"
            >
              Pause
            </button>
          )}

          {phase !== 'idle' && (
            <button
              type="button"
              onClick={handleStop}
              className="px-3 py-2 text-stone-500 hover:text-coral-400 font-medium transition-colors"
            >
              Stop
            </button>
          )}
        </div>
      </div>

      {/* Main game area */}
      <div className="flex flex-1 relative">
        {/* Canvas container */}
        <div ref={containerRef} className="absolute inset-0 flex items-center justify-center bg-studio-950 overflow-hidden">
          {/* Audio Init Overlay - shown when browser requires user interaction */}
          {phase === 'idle' && needsUserInteraction && (
            <AudioInitOverlay 
              trackTitle={midiTrack?.title}
              onInit={handleAudioInitAndStart}
              isVisible={needsUserInteraction}
            />
          )}

          {/* Loading Bar - shown while downloading audio */}
          {phase === 'idle' && audioIsLoading && !needsUserInteraction && (
            <LoadingBar progress={audioProgress} status="Loading audio track..." trackTitle={midiTrack?.title} />
          )}
          
          <canvas
            ref={canvasRef}
            className="block game-canvas"
            style={{
              background: 'linear-gradient(to bottom, #0a0a0b, #111111)',
              visibility: (phase === 'idle' && audioIsLoading) ? 'hidden' : 'visible',
            }}
          />

          {/* Touch zones overlay */}
          <div
            ref={touchZonesRef}
            className="absolute inset-0 pointer-events-auto"
            style={{ touchAction: 'none' }}
          >
            <TouchZones
              activeLanes={touchActiveLanes}
              isVisible={isTouchMode && (phase === 'playing' || phase === 'countdown')}
            />
          </div>

          {/* Countdown overlay */}
          {phase === 'countdown' && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div
                key={countdownBeat}
                className="font-display text-8xl font-bold animate-fade-in"
                style={{ color: '#f5a623', textShadow: '0 0 40px rgba(245, 166, 35, 0.3)' }}
              >
                {4 - countdownBeat + 1 <= 4 && 4 - countdownBeat >= 0
                  ? 4 - countdownBeat === 0 ? 'GO' : 4 - countdownBeat
                  : ''}
              </div>
            </div>
          )}

          {/* Pause overlay */}
          {phase === 'paused' && (
            <div className="absolute inset-0 bg-studio-950/90 flex flex-col items-center justify-center gap-6 pointer-events-auto">
              <h2 className="font-display text-5xl font-bold text-stone-100">Paused</h2>
              <div className="flex gap-4">
                <button
                  onClick={() => void handleResume()}
                  className="px-8 py-3 bg-amber-500 text-studio-950 font-semibold rounded-xl hover:bg-amber-400 text-lg"
                >
                  Resume
                </button>
                <button
                  onClick={handleStop}
                  className="px-8 py-3 bg-studio-800 text-stone-300 font-semibold rounded-xl hover:bg-studio-700"
                >
                  Quit
                </button>
              </div>
            </div>
          )}

          {/* Error state */}
          {hasError && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <p className="text-coral-400 text-lg mb-4">{hasError}</p>
                <button onClick={() => navigate('/')} className="btn-secondary">
                  Back to Home
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar - Hidden on mobile */}
        {!isMobile && (
          <div className="w-56 bg-studio-950 border-l border-studio-800 flex flex-col">
            <div className="flex-1 px-4 py-4">
              <HitZone activeLanes={activeLanes} lastRatings={lastRatings} />
            </div>
            <div className="px-4 py-5 border-t border-studio-800">
              <ScoreDisplay
                score={gameState.score}
                combo={gameState.combo}
                maxCombo={gameState.maxCombo}
                lastRating={lastRating}
              />
            </div>
          </div>
        )}
      </div>

      {/* Mobile score overlay */}
      {isMobile && phase !== 'idle' && (
        <div className="fixed top-20 right-4 bg-studio-950/90 backdrop-blur-sm rounded-xl p-3 border border-studio-800">
          <ScoreDisplay
            score={gameState.score}
            combo={gameState.combo}
            maxCombo={gameState.maxCombo}
            lastRating={lastRating}
          />
        </div>
      )}

      {/* Bottom status bar */}
      <div className="flex items-center px-4 py-2 bg-studio-950 border-t border-studio-800 text-xs text-stone-500 gap-4">
        <span className="font-mono">
          {gameState.notes.filter((n) => n.judged).length}/{gameState.notes.length}
        </span>
        <div className="flex gap-4">
          <span className="text-sage-400">P: {gameState.perfectCount}</span>
          <span className="text-gold-400">G: {gameState.goodCount}</span>
          <span className="text-coral-400">M: {gameState.missCount}</span>
        </div>
        <div className="h-1.5 bg-studio-800 rounded-full overflow-hidden flex-1 mx-4">
          <div
            className="h-full bg-amber-500 rounded-full transition-none"
            style={{
              width: `${Math.min(100, (currentTimeMs / 1000 / ((sectionEnd ?? 20) - sectionStart)) * 100)}%`,
            }}
          />
        </div>
        <span className="font-mono">
          {Math.floor(currentTimeMs / 1000)}s / {Math.round((sectionEnd ?? 20) - sectionStart)}s
        </span>
      </div>
    </div>
  );
};
